import { useEffect, useRef, useState } from 'react';
import { Loader2, ArrowDown } from 'lucide-react';

// Mobile pull-to-refresh: drag down at the top of the page to reload data.
export default function PullToRefresh({ onRefresh, children }) {
  const [pull, setPull] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const startY = useRef(null);
  const pullRef = useRef(0);
  const refreshingRef = useRef(false);
  const onRefreshRef = useRef(onRefresh);
  onRefreshRef.current = onRefresh;
  const THRESHOLD = 70;

  const setPullVal = (v) => { pullRef.current = v; setPull(v); };
  const setRefreshingVal = (v) => { refreshingRef.current = v; setRefreshing(v); };

  useEffect(() => {
    const getScroller = () => document.querySelector('.App');
    const atTop = () => {
      const s = getScroller();
      return (s ? s.scrollTop : (window.scrollY || document.documentElement.scrollTop || 0)) <= 0;
    };
    const onStart = (e) => {
      if (refreshingRef.current || !atTop()) { startY.current = null; return; }
      startY.current = e.touches[0].clientY;
    };
    const onMove = (e) => {
      if (startY.current == null || refreshingRef.current) return;
      const dy = e.touches[0].clientY - startY.current;
      if (dy > 0 && atTop()) setPullVal(Math.min(dy * 0.5, 90));
      else if (dy <= 0) setPullVal(0);
    };
    const onEnd = async () => {
      if (startY.current == null) return;
      const shouldRefresh = pullRef.current >= THRESHOLD;
      startY.current = null;
      if (shouldRefresh) {
        setRefreshingVal(true);
        setPullVal(48);
        try { await onRefreshRef.current?.(); } catch (e) { /* silent */ }
        setRefreshingVal(false);
      }
      setPullVal(0);
    };
    window.addEventListener('touchstart', onStart, { passive: true });
    window.addEventListener('touchmove', onMove, { passive: true });
    window.addEventListener('touchend', onEnd, { passive: true });
    return () => {
      window.removeEventListener('touchstart', onStart);
      window.removeEventListener('touchmove', onMove);
      window.removeEventListener('touchend', onEnd);
    };
  }, []);

  const ready = pull >= THRESHOLD;
  return (
    <>
      <div
        className="fixed top-0 left-0 right-0 z-[60] flex items-end justify-center pointer-events-none"
        style={{
          height: pull,
          opacity: pull > 4 ? 1 : 0,
          transition: (refreshing || pull === 0) ? 'height 0.2s ease, opacity 0.2s ease' : 'none',
        }}
        data-testid="pull-to-refresh-indicator"
      >
        <div className="mb-2 w-9 h-9 rounded-full bg-white shadow-md border border-slate-100 flex items-center justify-center">
          {refreshing ? (
            <Loader2 size={18} className="animate-spin text-emerald-500" />
          ) : (
            <ArrowDown size={18} className={`text-emerald-500 transition-transform ${ready ? 'rotate-180' : ''}`} />
          )}
        </div>
      </div>
      {children}
    </>
  );
}
